/**
 * Mega Menu Component
 * Handles hover/click behavior for desktop and mobile
 */
(function() {
  'use strict';

  class MegaMenu {
    constructor() {
      this.menuTriggers = document.querySelectorAll('[data-mega-menu-trigger]');
      this.megaMenus = document.querySelectorAll('[data-mega-menu]');
      this.activeMenu = null;
      this.activeTrigger = null;
      this.isMobile = window.innerWidth <= 989;
      this.hoverTimeout = null;

      this.init();
    }

    init() {
      if (this.menuTriggers.length === 0) return;

      // Detect mobile/desktop
      window.addEventListener('resize', () => {
        this.isMobile = window.innerWidth <= 989;
      });

      // Setup triggers
      this.menuTriggers.forEach(trigger => {
        const menuId = trigger.getAttribute('data-mega-menu-trigger');
        // Use data-menu-id instead of data-mega-menu for the selector
        const menu = document.querySelector(`[data-menu-id="${menuId}"]`);
        
        if (!menu) {
          // No menu found, allow normal navigation
          return;
        }
        
        // Check if menu has any content - if not, allow normal navigation
        const hasContent = menu.querySelectorAll('.mega-menu__category-item, .mega-menu__categories-grid a, .mega-menu__categories-grid > *, a').length > 0;
        
        if (!hasContent) {
          // Menu exists but has no content, allow normal navigation
          return;
        }

        // Desktop and Mobile: click to toggle (no hover)
        trigger.addEventListener('click', (e) => {
          // Always prevent navigation for items with mega menus
          e.preventDefault();
          e.stopPropagation();
          
          // Toggle menu on click for both desktop and mobile
          this.toggleMenu(trigger, menu);
          
          return false;
        }); // Don't use capture phase - let it run after global handler

        // Keyboard navigation
        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggleMenu(trigger, menu);
          }
        });
      });

      // Image switching removed - now using single image per menu from customizer

      // Close menu when clicking outside (for both desktop and mobile)
      this.megaMenus.forEach(menu => {

        // Close menu when clicking on category items (desktop)
        const categoryItems = menu.querySelectorAll('.mega-menu__category-item');
        categoryItems.forEach(item => {
          item.addEventListener('click', () => {
            if (!this.isMobile) {
              // Small delay to allow navigation
              setTimeout(() => {
                this.closeMenu(this.activeTrigger, menu);
              }, 100);
            }
          });
        });
      });

      // Close on outside click (for both desktop and mobile)
      document.addEventListener('click', (e) => {
        if (this.activeMenu) {
          const clickedInsideMenu = this.activeMenu.contains(e.target);
          const clickedOnTrigger = this.activeTrigger?.contains(e.target);
          
          if (!clickedInsideMenu && !clickedOnTrigger) {
            this.closeMenu(this.activeTrigger, this.activeMenu);
          }
        }
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.activeMenu) {
          this.closeMenu(this.activeTrigger, this.activeMenu);
          this.activeTrigger?.focus();
        }
      });
    }


    openMenu(trigger, menu) {
      // Clear any pending close
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
      }

      // Close other menus
      if (this.activeMenu && this.activeMenu !== menu) {
        this.closeMenu(this.activeTrigger, this.activeMenu);
      }

      // Calculate and update position for fixed menu to align with navigation bar
      if (!this.isMobile && trigger) {
        this.updateMenuPosition(menu, trigger);
        // Update position on scroll and resize to keep aligned with navigation bar
        const updatePosition = () => {
          if (this.activeMenu === menu) {
            this.updateMenuPosition(menu, trigger);
          }
        };
        window.addEventListener('scroll', updatePosition, { passive: true });
        window.addEventListener('resize', updatePosition, { passive: true });
        // Store cleanup function
        menu._scrollHandler = updatePosition;
        menu._resizeHandler = updatePosition;
      }

      // Open this menu
      menu.classList.add('active');
      menu.setAttribute('aria-hidden', 'false');
      menu.style.display = 'block'; // Ensure it's visible
      this.activeMenu = menu;
      this.activeTrigger = trigger;
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
      }
    }

    updateMenuPosition(menu, trigger) {
      if (!trigger) return;
      const triggerRect = trigger.getBoundingClientRect();
      const navBar = trigger.closest('.header__navigation-bar');
      const navRect = navBar ? navBar.getBoundingClientRect() : null;
      const menuTop = navRect ? navRect.bottom : triggerRect.bottom;
      const viewportWidth = window.innerWidth;
      const edgePadding = 16;
      const minMenuWidth = 476;
      const maxMenuWidth = viewportWidth - edgePadding * 2;

      menu.style.top = menuTop + 'px';
      menu.style.transform = 'none';
      menu.style.maxWidth = maxMenuWidth + 'px';

      // Temporarily make menu measurable if hidden (avoids using wrong width)
      const wasHidden = !menu.classList.contains('active');
      if (wasHidden) {
        menu.style.visibility = 'hidden';
        menu.style.display = 'block';
      }
      const menuWidth = Math.min(menu.offsetWidth || minMenuWidth, maxMenuWidth);
      if (wasHidden) {
        menu.style.visibility = '';
        menu.style.display = '';
      }

      var largeMenuThreshold = 600;
      var left;
      if (menuWidth >= largeMenuThreshold) {
        left = Math.max(edgePadding, (viewportWidth - menuWidth) / 2);
      } else {
        left = triggerRect.left;
        if (left + menuWidth > viewportWidth - edgePadding) {
          left = Math.max(edgePadding, viewportWidth - menuWidth - edgePadding);
        } else if (left < edgePadding) {
          left = edgePadding;
        }
      }
      menu.style.left = left + 'px';
    }

    closeMenu(trigger, menu) {
      menu.classList.remove('active');
      menu.setAttribute('aria-hidden', 'true');
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      }
      
      // Remove scroll and resize listeners
      if (menu._scrollHandler) {
        window.removeEventListener('scroll', menu._scrollHandler);
        menu._scrollHandler = null;
      }
      if (menu._resizeHandler) {
        window.removeEventListener('resize', menu._resizeHandler);
        menu._resizeHandler = null;
      }
      
      if (this.activeMenu === menu) {
        this.activeMenu = null;
        this.activeTrigger = null;
      }
    }

    closeMenuWithDelay(trigger, menu) {
      this.hoverTimeout = setTimeout(() => {
        this.closeMenu(trigger, menu);
      }, 200);
    }

    toggleMenu(trigger, menu) {
      if (menu.classList.contains('active')) {
        this.closeMenu(trigger, menu);
      } else {
        // Close other menus first
        if (this.activeMenu && this.activeMenu !== menu) {
          this.closeMenu(this.activeTrigger, this.activeMenu);
        }
        this.openMenu(trigger, menu);
      }
    }
  }

  // Initialize immediately and also on DOM ready
  function initMegaMenu() {
    new MegaMenu();
  }

  // Global click handler as fallback - only prevent navigation
  // This runs in capture phase to catch early and prevent navigation
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('.mega-menu-trigger-link');
    if (trigger) {
      const menuId = trigger.getAttribute('data-mega-menu-trigger');
      const menu = document.querySelector(`[data-menu-id="${menuId}"]`);
      
      if (menu) {
        const hasContent = menu.querySelectorAll('.mega-menu__category-item, .mega-menu__categories-grid a, a').length > 0;
        
        if (hasContent) {
          // Only prevent navigation, let the main handler toggle the menu
          e.preventDefault();
          // Don't stop propagation - let the main handler run
        }
      }
    }
  }, true); // Use capture phase

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMegaMenu);
  } else {
    initMegaMenu();
  }
})();
